import { checkChartEligibility, readChartEligibility, rememberChartEligibility, requireChartOfflineEligibility, eligibilityError, ONLINE_ONLY_MESSAGE } from './chartEligibility';
import { listEditorRecoveries, discardEditorRecoveries, reconcileEditorRecoveries } from './editorRecovery';
import config from '../config';
import { getOfflineOwnerIdentity, hasFreshOfflineIdentity } from './offlineIdentity';
import { isOfflineVaultUnlocked, resumeOfflineVaultSession } from './vault';
import { encryptClinicalPayload, decryptClinicalPayload } from './clinicalEncryption';
import { compareAndSwapRecord, listStoredRecords, newOperationId, readStoredRecord, storageKey } from './durableStore';
import { applyClinicalPatch, clinicalChanges, clinicalEqual, mergeClinicalValues, withoutConflictedFields } from './clinicalMerge';
import { captureClinicalTime, recordServerClockSample, clockReviewStatus } from './clinicalClock';
import { getActiveLaborContinuities } from './offlineSession';
import { registerAdditionalPreparedCharts, setPreparedChartPin, cleanupPreparedCharts, PREPARED_CHART_TTL_MS } from './preparedChartStore';

const resourceKey = (type, ref) => `${type}:${ref}`;
const announce = () => window.dispatchEvent(new CustomEvent('vela:clinical-repository-changed'));
const mutationErrorMessage = body => {
  const direct = [body.message, body.error].find(value => typeof value === 'string' && value);
  if (direct) return direct;
  const errors = Array.isArray(body.errors) ? body.errors : Object.values(body.errors || {}).flat();
  return errors.filter(value => typeof value === 'string').join(' ') || 'This change needs review before it can sync.';
};
const running = new Map();
let triggerInstalled = false;
let apiReachable = true;
let connectivityGeneration = 0;
let connectivityProbe = null;
const activeSyncRequests = new Set();
async function probeConnectivity() {
  if (connectivityProbe) return connectivityProbe;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 3000);
  connectivityProbe = fetch(`${config.api.url.replace(/\/api$/, '')}/health`, { cache: 'no-store', signal: controller.signal })
    .then(response => response.ok).catch(() => false).finally(() => { window.clearTimeout(timeout); connectivityProbe = null; });
  return connectivityProbe;
}
const syncProblems = new Map();
const problemKey = client => `${getOfflineOwnerIdentity()?.ownerKey}:${client}`;
function reportSyncProblem(client, message) {
  const key = problemKey(client);
  if ((syncProblems.get(key) || null) === message) return;
  if (message) syncProblems.set(key, message); else syncProblems.delete(key);
  announce();
}
const bytesOf = value => new TextEncoder().encode(JSON.stringify(value)).byteLength;
const isPinned = chart => Boolean(chart.pending.length || Object.values(chart.resources).some(item => item.conflicts?.length || item.mrnStatus === 'pending' || item.clockReview)
  || getActiveLaborContinuities().some(item => item.clientReferenceId === chart.clientReferenceId));
registerAdditionalPreparedCharts(async owner => {
  const records = await listStoredRecords(owner.ownerKey, 'clinical-chart');
  const active = new Set(getOfflineOwnerIdentity()?.ownerKey === owner.ownerKey
    ? getActiveLaborContinuities().map(item => item.clientReferenceId) : []);
  return Promise.all(records.map(async record => {
    let clientReferenceId = record.clientReferenceId;
    let retained = record.retainedWork;
    // Old metadata is never guessed. Inspect only the current unlocked owner,
    // and otherwise pin the record until it can be safely identified.
    if ((!clientReferenceId || retained === undefined) && isOfflineVaultUnlocked(owner)) {
      try {
        const chart = await decryptClinicalPayload(record.payload);
        clientReferenceId = chart.clientReferenceId;
        retained = Boolean(chart.pending.length || Object.values(chart.resources).some(item => item.conflicts?.length || item.mrnStatus === 'pending' || item.clockReview));
      } catch { retained = true; }
    }
    return { id: record.key, clientReferenceId, bytes: record.bytes || bytesOf(record.payload),
      lastOpenedAt: record.lastOpenedAt || 0, expiresAt: (record.lastOpenedAt || 0) + PREPARED_CHART_TTL_MS,
      pinned: retained !== false || !clientReferenceId || active.has(clientReferenceId),
      remove: () => compareAndSwapRecord(record.key, record.storageRevision, null),
    };
  }));
});

export async function cleanupClinicalCharts({ preserveKey = null, incomingBytes = 0, clientReferenceId = null } = {}) {
  if (!getOfflineOwnerIdentity() || !isOfflineVaultUnlocked()) return;
  const owner = identity();
  return cleanupPreparedCharts({ owner, incomingChart: preserveKey ? {
    id: preserveKey, clientReferenceId: clientReferenceId || `incoming:${preserveKey}`, bytes: incomingBytes,
    pinned: getActiveLaborContinuities().some(item => item.clientReferenceId === clientReferenceId),
  } : null });
}

function valueAt(value, path) {
  for (const key of path.match(/[^.[\]]+/g) || []) value = Array.isArray(value)
    ? value.find(item => String(item.referenceId ?? item.ReferenceId ?? item.clientEntryId ?? item.id ?? item.Id) === key) : value?.[key];
  return value;
}
function retainConflicts(previous, incoming, local, server) {
  const conflicts = new Map([...previous, ...incoming].map(item => [item.path, item]));
  return [...conflicts.values()].filter(item => item.identityConflict || !item.path || !clinicalEqual(valueAt(local, item.path), item.origin === 'device' ? item.server : valueAt(server, item.path), item.path))
    .map(item => item.identityConflict || !item.path ? item : { ...item, local: valueAt(local, item.path), server: item.origin === 'device' ? item.server : valueAt(server, item.path) });
}

export async function purgeCleanClinicalCharts(owner = getOfflineOwnerIdentity()) {
  if (!owner) return;
  for (const record of await listStoredRecords(owner.ownerKey, 'clinical-chart')) {
    // Unknown older metadata is retained until it can be unlocked and inspected.
    if (record.retainedWork === false) await compareAndSwapRecord(record.key, record.storageRevision, null);
  }
}
window.addEventListener('vela:offline-owner-logged-out', event => {
  void purgeCleanClinicalCharts(event.detail?.owner).catch(() => {});
});

function identity() {
  const owner = getOfflineOwnerIdentity();
  if (!owner || !isOfflineVaultUnlocked(owner)) throw new Error('Unlock this account’s device charts before continuing.');
  return owner;
}

function assertOwner(owner) {
  if (identity().ownerKey !== owner.ownerKey) throw new Error('Practice access changed while saving.');
}

async function chartKey(owner, clientReferenceId) {
  return storageKey(owner.ownerKey, 'clinical-chart', clientReferenceId);
}

async function readChart(clientReferenceId) {
  const owner = identity();
  const stored = await readStoredRecord(await chartKey(owner, clientReferenceId));
  if (!stored || stored.owner !== owner.ownerKey) return null;
  const chart = await decryptClinicalPayload(stored.payload);
  assertOwner(owner);
  if (!isPinned(chart) && (stored.lastOpenedAt || 0) + PREPARED_CHART_TTL_MS <= Date.now()) return null;
  return chart;
}

async function editChart(clientReferenceId, change, { preparation = false } = {}) {
  const owner = identity();
  const key = await chartKey(owner, clientReferenceId);
  for (let retry = 0; retry < 12; retry += 1) {
    const stored = await readStoredRecord(key);
    const chart = stored ? await decryptClinicalPayload(stored.payload)
      : { clientReferenceId, pregnancies: [], resources: {}, pending: [], reviews: [], capabilities: [] };
    const result = change(chart);
    const payload = await encryptClinicalPayload(chart, { requireVault: true });
    assertOwner(owner);
    const bytes = bytesOf(payload);
    if (preparation) {
      const capacity = await cleanupClinicalCharts({ preserveKey: key, incomingBytes: bytes, clientReferenceId });
      if (!isPinned(chart) && capacity.bytes > capacity.budget) throw new Error('Device storage is reserved for active episodes and unsynced work. Sync existing work before preparing another chart.');
    }
    if (await compareAndSwapRecord(key, stored?.storageRevision || 0, {
      owner: owner.ownerKey, kind: 'clinical-chart', clientReferenceId, payload, bytes, pendingCount: chart.pending.filter(item => item.status === 'pending').length,
      retainedWork: Boolean(chart.pending.length || Object.values(chart.resources).some(item => item.conflicts?.length || item.mrnStatus === 'pending' || item.clockReview)),
      lastOpenedAt: preparation ? Date.now() : stored?.lastOpenedAt || Date.now(),
    })) {
      announce();
      const pinned = Boolean(chart.pending.length || Object.values(chart.resources).some(item => item.conflicts?.length || item.mrnStatus === 'pending' || item.clockReview));
      for (const pregnancyReferenceId of [null, ...chart.pregnancies]) {
        await setPreparedChartPin({ clientReferenceId, pregnancyReferenceId }, 'clinical-pending', pinned).catch(() => {});
      }
      return result;
    }
  }
  throw new Error('Another tab is updating this chart. Your editor is unchanged; try saving again.');
}

const stateFor = resource => resource?.conflicts?.length ? 'needs-review'
  : resource?.pending ? 'saved-on-device' : 'synced';

function singletonPeer(chart, resource) {
  const type = resource.resourceType === 'laborEpisode' ? 'birthEvent'
    : resource.resourceType === 'birthEvent' ? 'laborEpisode' : null;
  return type ? chart.resources[resourceKey(type, resource.referenceId)] : null;
}

function physicalResourceKeys(chart, resource) {
  const peer = singletonPeer(chart, resource);
  return [resourceKey(resource.resourceType, resource.referenceId), ...(peer ? [resourceKey(peer.resourceType, peer.referenceId)] : [])];
}

function mirrorSingletonLocal(chart, resource) {
  const peer = singletonPeer(chart, resource);
  if (!peer) return;
  // The singleton episode and its baby's birth record name one physical row.
  // Mirror its local values in the same IndexedDB commit, before an offline
  // reader can reopen the other representation. Keep both server bases intact.
  const physical = resource.resourceType === 'laborEpisode' ? resource.local.primaryBirthEvent : resource.local;
  if (physical) {
    const fields = { ...physical };
    delete fields.deleted;
    peer.local = applyClinicalPatch(peer.local, peer.resourceType === 'laborEpisode'
      ? { primaryBirthEvent: fields } : fields);
  }
  if (resource.local.deleted) peer.local.deleted = true;
  else delete peer.local.deleted;
  if (peer.resourceType === 'laborEpisode' && peer.local.primaryBirthEvent) delete peer.local.primaryBirthEvent.deleted;
  peer.conflicts = resource.conflicts.map(conflict => ({ ...conflict, path: !conflict.path ? ''
    : peer.resourceType === 'laborEpisode' ? `primaryBirthEvent.${conflict.path}`
      : conflict.path.replace(/^primaryBirthEvent\.?/, '') }));
  const keys = physicalResourceKeys(chart, resource);
  resource.pending = peer.pending = chart.pending.some(item => keys.includes(item.key));
}

export async function getClinicalResource({ clientReferenceId, resourceType, resourceReferenceId }) {
  const chart = await readChart(clientReferenceId);
  const key = resourceKey(resourceType, resourceReferenceId);
  const resource = chart?.resources[chart?.aliases?.[key] || key];
  return resource ? { ...resource, data: resource.local, status: stateFor(resource) } : null;
}

export async function listClinicalResources({ clientReferenceId, resourceType, parentReferenceId }) {
  const chart = await readChart(clientReferenceId);
  return Object.values(chart?.resources || {}).filter(resource => (!resourceType || resource.resourceType === resourceType)
    && (!parentReferenceId || resource.parentReferenceId === parentReferenceId))
    .map(resource => ({ ...resource, data: resource.local, status: stateFor(resource) }));
}

const preparations = new Map();
export function prepareChart(clientReferenceId, pregnancyReferenceId = null) {
  // Offline startup (or a temporarily unavailable API) must still install the
  // reconnect/retry listeners for work retained from an earlier page load.
  installClinicalSyncTriggers();
  const key = JSON.stringify([getOfflineOwnerIdentity()?.ownerKey, clientReferenceId, pregnancyReferenceId]);
  if (preparations.has(key)) return preparations.get(key);
  const task = prepareChartOnce(clientReferenceId, pregnancyReferenceId).finally(() => preparations.delete(key));
  preparations.set(key, task);
  return task;
}

async function prepareChartOnce(clientReferenceId, pregnancyReferenceId = null) {
  if (!hasFreshOfflineIdentity() || !isOfflineVaultUnlocked()) return null;
  const owner = identity();
  const query = new URLSearchParams({ protocolVersion: '1' });
  if (pregnancyReferenceId) query.set('pregnancyReferenceId', pregnancyReferenceId);
  const sentAt = Date.now();
  const response = await fetch(`${config.api.url}/clients/${encodeURIComponent(clientReferenceId)}/offline-chart?${query}`, {
    headers: { Authorization: `Bearer ${owner.token}`, 'X-Sena-Offline-Prepare': 'true' },
  });
  assertOwner(owner);
  if ([404, 501, 503].includes(response.status)) return null; // API rollout has not enabled this protocol yet.
  if (!response.ok) {
    const problem = await response.json().catch(() => ({}));
    if (problem.code === 'chart_online_only') { await rememberChartEligibility(clientReferenceId, false); throw eligibilityError(); }
    throw new Error('This chart could not be prepared. Reconnect and try again.');
  }
  const bundle = await response.json();
  if (bundle.protocolVersion !== 1 || !Array.isArray(bundle.resources)) return null;
  await rememberChartEligibility(clientReferenceId, true);
  recordServerClockSample(bundle.preparedAtUtc, sentAt);
  await editChart(clientReferenceId, chart => {
    chart.capabilities = bundle.supportedResourceTypes || [];
    chart.preparedAt = bundle.preparedAtUtc;
    chart.pregnancyAliases = bundle.pregnancies || chart.pregnancyAliases || [];
    for (const pregnancy of chart.pregnancyAliases) if (!chart.pregnancies.includes(pregnancy.referenceId)) chart.pregnancies.push(pregnancy.referenceId);
    if (pregnancyReferenceId && !chart.pregnancies.includes(pregnancyReferenceId)) chart.pregnancies.push(pregnancyReferenceId);
    const incomingKeys = new Set(bundle.resources.map(item => resourceKey(item.resourceType, item.referenceId)));
    for (const [key, previous] of Object.entries(chart.resources)) {
      if ((!pregnancyReferenceId || previous.pregnancyReferenceId === pregnancyReferenceId) && !incomingKeys.has(key)
        && !previous.pending && !previous.conflicts?.length && previous.mrnStatus !== 'pending' && !previous.clockReview) delete chart.resources[key];
    }
    for (const incoming of bundle.resources) {
      const key = resourceKey(incoming.resourceType, incoming.referenceId);
      const existing = chart.resources[key];
      const server = incoming.payload;
      if (existing && incoming.revision < existing.revision) continue;
      if (!existing) {
        chart.resources[key] = { ...incoming, base: server, local: server, conflicts: [], pending: false, mrnStatus: server.mrnStatus || (server.pendingNewbornMRN ? 'pending' : 'resolved'), pendingMRN: server.pendingNewbornMRN };
      } else {
        const merged = mergeClinicalValues(existing.base, existing.local, server);
        // Attempted mutations keep their original base and exact request bytes.
        // Reconciliation is performed when their receipt is known.
        if (chart.pending.some(mutation => mutation.key === key && mutation.sealedAt)) continue;
        const conflicts = retainConflicts(existing.conflicts, merged.conflicts, merged.value, server);
        Object.assign(existing, incoming, { base: server, local: merged.value, conflicts, mrnStatus: server.mrnStatus || (server.pendingNewbornMRN ? 'pending' : existing.mrnStatus), pendingMRN: server.pendingNewbornMRN });
        for (const mutation of chart.pending.filter(item => item.key === key && !item.sealedAt)) {
          mutation.baseRevision = incoming.revision;
          mutation.baseValues = server;
          mutation.payload = withoutConflictedFields(clinicalChanges(server, merged.value), existing.conflicts);
        }
      }
    }
  }, { preparation: true });
  await reconcileClinicalEditorCopies(clientReferenceId);
  await cleanupPreparedCharts();
  installClinicalSyncTriggers();
  void syncClinicalChart(clientReferenceId);
  return bundle;
}

export async function resolveClinicalMutationContext(input) {
  if (!getOfflineOwnerIdentity() || !isOfflineVaultUnlocked()) return null;
  const owner = identity();
  const records = await listStoredRecords(owner.ownerKey, 'clinical-chart');
  for (const record of records) {
    const chart = await decryptClinicalPayload(record.payload);
    assertOwner(owner);
    if (!chart.capabilities.includes(input.resourceType)) continue;
    if (input.clientReferenceId && input.clientReferenceId !== chart.clientReferenceId) continue;
    if (!isPinned(chart) && (record.lastOpenedAt || 0) + PREPARED_CHART_TTL_MS <= Date.now()) continue;
    const resources = Object.values(chart.resources);
    const parentTypes = { birthEvent: ['baby'], laborRecord: ['birthEvent', 'laborEpisode'], fhtSession: ['birthEvent', 'laborEpisode'], resuscitation: ['birthEvent', 'laborEpisode'], hemorrhage: ['birthEvent', 'laborEpisode'], postpartumAssessment: ['birthEvent', 'laborEpisode'], ballardAssessment: ['baby'], resuscitationIntervention: ['resuscitation'], hemorrhageEvent: ['hemorrhage'] };
    const pregnancy = chart.pregnancyAliases?.find(item => [item.id, item.referenceId].some(id => id != null && String(id) === String(input.parentReferenceId)));
    const parent = resources.find(resource => parentTypes[input.resourceType]?.includes(resource.resourceType) && [resource.referenceId, resource.local?.id, resource.local?.Id]
      .some(id => id != null && String(id) === String(input.parentReferenceId)));
    const parentReferenceId = parent?.referenceId || pregnancy?.referenceId || input.parentReferenceId;
    const singleton = ['resuscitation', 'hemorrhage', 'postpartumAssessment'].includes(input.resourceType);
    const aliased = chart.resources[chart.aliases?.[resourceKey(input.resourceType, input.resourceReferenceId)]];
    const candidate = aliased || resources.find(resource => resource.resourceType === input.resourceType
      && [resource.referenceId, resource.local?.id, resource.local?.Id].some(id => id != null && String(id) === String(input.resourceReferenceId)));
    const matched = candidate || (singleton && resources.find(resource => resource.resourceType === input.resourceType
      && resource.parentReferenceId === parentReferenceId));
    const parentKnown = parentReferenceId && (String(parentReferenceId) === String(chart.clientReferenceId) || chart.pregnancies.includes(parentReferenceId)
      || resources.some(resource => resource.referenceId === parentReferenceId));
    if (!matched && !parentKnown && !(input.clientReferenceId && !input.parentReferenceId)) continue;
    const eligibility = await checkChartEligibility(chart.clientReferenceId);
    if (eligibility?.eligible === false) {
      if (!navigator.onLine) throw eligibilityError();
      return null; // Online-only charts use the existing server CRUD path, never a device overlay.
    }
    if (eligibility?.eligible !== true) await requireChartOfflineEligibility(chart.clientReferenceId);
    return { clientReferenceId: chart.clientReferenceId,
      pregnancyReferenceId: input.pregnancyReferenceId || matched?.pregnancyReferenceId || parent?.pregnancyReferenceId
        || (chart.pregnancies.includes(parentReferenceId) ? parentReferenceId : null),
      resourceReferenceId: matched?.referenceId || input.resourceReferenceId,
      parentReferenceId: matched?.parentReferenceId || parentReferenceId,
      baseRevision: matched?.revision || 0, baseValues: matched?.local || {},
    };
  }
  return null;
}

export async function commitClinicalMutation(input) {
  const clientReferenceId = input.clientReferenceId || input.context?.clientReferenceId;
  const resourceReferenceId = input.resourceReferenceId || input.resourceId;
  if (!clientReferenceId || !resourceReferenceId) throw new Error('The chart identity is not ready for automatic device saving.');
  await requireChartOfflineEligibility(clientReferenceId);
  // Recovery is separate from the mutation queue. A stale/unreadable editor
  // copy must never prevent another record from being submitted.
  await reconcileClinicalEditorCopies(clientReferenceId).catch(() => {});
  const owner = identity();
  const operation = input.operation || (input.method === 'POST' ? 'create' : input.method === 'DELETE' ? 'delete' : 'update');
  const payload = input.payload || (typeof input.body === 'string' ? JSON.parse(input.body) : input.body) || {};
  const mutationId = newOperationId();
  const captureMetadata = input.captureMetadata || captureClinicalTime();
  const key = resourceKey(input.resourceType, resourceReferenceId);
  const result = await editChart(clientReferenceId, chart => {
    if (!chart.capabilities.includes(input.resourceType)) throw new Error('This chart section is not enabled for automatic offline sync yet.');
    const resource = chart.resources[key] || { resourceType: input.resourceType, referenceId: resourceReferenceId,
      parentReferenceId: input.parentReferenceId, pregnancyReferenceId: input.pregnancyReferenceId,
      revision: input.baseRevision || 0, base: input.baseValues || {}, local: input.baseValues || {}, conflicts: [] };
    if (operation === 'create' && input.resourceType === 'laborEpisode') {
      payload.primaryBirthEvent = { ...payload.primaryBirthEvent,
        createdAt: payload.primaryBirthEvent?.createdAt || captureMetadata.deviceCapturedAt || new Date().toISOString() };
    }
    const physicalKeys = physicalResourceKeys(chart, resource);
    const rejected = chart.pending.filter(item => physicalKeys.includes(item.key) && item.status === 'needs-review' && [400, 422].includes(item.rejectionStatus));
    if (rejected.some(item => item.operation === 'create' && item.key !== key)) {
      throw new Error('Correct the original creation form before saving this linked record. Its saved device copy is retained.');
    }
    const retryingCreate = rejected.some(item => item.operation === 'create');
    if (rejected.length) {
      chart.reviews.push(...rejected.map(item => ({ kind: 'corrected-validation', mutationId: item.mutationId, payload: item.payload, reviewedAt: new Date().toISOString() })));
      chart.pending = chart.pending.filter(item => !rejected.includes(item));
      resource.conflicts = resource.conflicts.filter(item => item.path || item.identityConflict);
    }
    const original = input.baseValues || resource.local;
    const desired = applyClinicalPatch(original, payload);
    const tabMerge = mergeClinicalValues(original, desired, resource.local);
    resource.local = operation === 'delete' ? { ...resource.local, deleted: true } : tabMerge.value;
    const tabConflicts = tabMerge.conflicts.map(item => ({ ...item, origin: JSON.stringify(item.server) === JSON.stringify(valueAt(resource.base, item.path)) ? 'server' : 'device' }));
    resource.conflicts = retainConflicts(resource.conflicts, tabConflicts, resource.local, resource.base);
    resource.local.referenceId = resourceReferenceId;
    resource.conflicts = retainConflicts(resource.conflicts, [], resource.local, resource.base);
    resource.pending = true;
    chart.resources[key] = resource;
    // Do not coalesce across a write through the other singleton alias. That
    // would invert the dependency order or create a dependency cycle.
    const latestPhysical = chart.pending.filter(item => physicalKeys.includes(item.key)).slice(-1)[0];
    const unsent = latestPhysical?.key === key && !latestPhysical.sealedAt && latestPhysical.status === 'pending' ? latestPhysical : null;
    if (operation === 'delete' && (retryingCreate || unsent?.operation === 'create') && !chart.pending.some(item => item.key === key && item.sealedAt)) {
      if (Object.values(chart.resources).some(item => item.parentReferenceId === resourceReferenceId && !item.local?.deleted)) {
        throw new Error('Remove the dependent entries before removing this new device record. Their saved copies are unchanged.');
      }
      chart.pending = chart.pending.filter(item => item.key !== key);
      resource.pending = false;
      resource.conflicts = [];
      mirrorSingletonLocal(chart, resource);
      return { data: resource.local, status: 'saved-on-device', operationId: mutationId, mutationId, revision: resource.revision };
    }
    const pendingCreate = chart.pending.find(item => item.key === key && item.operation === 'create');
    const predecessors = chart.pending.filter(item => physicalKeys.includes(item.key) && item !== unsent
      && (item.status === 'pending' || item.operation === 'create'));
    const mutation = {
      protocolVersion: 1, mutationId: unsent?.mutationId || mutationId, key,
      resourceType: input.resourceType, resourceReferenceId,
      parentReferenceId: input.parentReferenceId || resource.parentReferenceId,
      pregnancyReferenceId: input.pregnancyReferenceId || resource.pregnancyReferenceId,
      operation: retryingCreate || unsent?.operation === 'create' ? 'create' : pendingCreate ? operation === 'delete' ? 'delete' : 'update' : operation,
      baseRevision: resource.revision, baseValues: resource.base,
      payload: operation === 'delete' ? {} : withoutConflictedFields(
        clinicalChanges(resource.base, resource.local) || {}, resource.conflicts),
      captureMetadata: unsent?.operation === 'create' ? unsent.captureMetadata : captureMetadata, status: 'pending', createdAt: unsent?.createdAt || new Date().toISOString(),
      dependsOn: [...new Set([...(input.dependsOn || []), ...predecessors.map(item => item.mutationId)])],
      attemptCount: 0, nextAttemptAt: null,
    };
    if (mutation.operation === 'update' && !Object.keys(mutation.payload).length && !predecessors.length) {
      if (unsent) chart.pending = chart.pending.filter(item => item !== unsent);
      resource.pending = chart.pending.some(item => physicalKeys.includes(item.key));
      mirrorSingletonLocal(chart, resource);
      return { data: resource.local, status: stateFor(resource), revision: resource.revision };
    }
    if (unsent) chart.pending[chart.pending.indexOf(unsent)] = mutation;
    else chart.pending.push(mutation);
    mirrorSingletonLocal(chart, resource);
    return { data: resource.local, status: stateFor(resource), operationId: mutation.mutationId,
      mutationId: mutation.mutationId, revision: resource.revision };
  });
  assertOwner(owner);
  installClinicalSyncTriggers();
  // The durable local write is complete. Give a healthy connection a brief
  // chance to acknowledge so online saves report their actual receipt state.
  const alreadySyncing = running.has(clientReferenceId);
  const syncing = syncClinicalChart(clientReferenceId);
  if (!alreadySyncing && navigator.onLine && hasFreshOfflineIdentity() && apiReachable) {
    let timer;
    try {
      await Promise.race([syncing, new Promise(resolve => { timer = window.setTimeout(resolve, 1500); })]);
      assertOwner(owner);
      const latest = await getClinicalResource({ clientReferenceId, resourceType: input.resourceType, resourceReferenceId });
      if (latest) return { ...result, data: latest.data, status: latest.status, revision: latest.revision };
    } finally { window.clearTimeout(timer); }
  }
  return result;
}

export async function tryCommitClinicalMutation(input) {
  const context = await resolveClinicalMutationContext(input);
  return context ? commitClinicalMutation({ ...context, ...input, clientReferenceId: context.clientReferenceId,
    resourceReferenceId: context.resourceReferenceId, parentReferenceId: context.parentReferenceId, pregnancyReferenceId: context.pregnancyReferenceId }) : null;
}

async function acquireLease(owner, kind, identityValue) {
  const key = await storageKey(owner.ownerKey, kind, identityValue);
  const stored = await readStoredRecord(key);
  if (stored?.expiresAt > Date.now()) return null;
  const lease = { owner: owner.ownerKey, kind, leaseId: newOperationId(), expiresAt: Date.now() + 60000 };
  if (!(await compareAndSwapRecord(key, stored?.storageRevision || 0, lease))) return null;
  return async () => {
    const current = await readStoredRecord(key);
    if (current?.leaseId === lease.leaseId) await compareAndSwapRecord(key, current.storageRevision, null);
  };
}

function requestBody(mutation) {
  const { key, status, createdAt, sealedAt, attemptCount, nextAttemptAt, dependsOn, authenticationRequired, rejectionStatus, rejectionCode, ...body } = mutation;
  return body;
}

async function synchronize(clientReferenceId) {
  if (!hasFreshOfflineIdentity() || !isOfflineVaultUnlocked() || !navigator.onLine || !apiReachable) return;
  const owner = identity();
  const available = await readChart(clientReferenceId);
  if (!available?.pending.some(item => item.status === 'pending' && !(Date.parse(item.nextAttemptAt) > Date.now()))) return;
  if ((await checkChartEligibility(clientReferenceId, { force: true }))?.eligible !== true) {
    reportSyncProblem(clientReferenceId, ONLINE_ONLY_MESSAGE); return;
  }
  const releaseChart = await acquireLease(owner, 'chart-sync-lease', clientReferenceId);
  if (!releaseChart) return;
  const releaseSlot = await acquireLease(owner, 'device-sync-slot', '0') || await acquireLease(owner, 'device-sync-slot', '1');
  if (!releaseSlot) { await releaseChart(); return; }
  try {
    let mutation;
    await editChart(clientReferenceId, chart => {
      mutation = chart.pending.find(item => item.status === 'pending' && !(Date.parse(item.nextAttemptAt) > Date.now())
        && !item.dependsOn?.some(id => chart.pending.some(parent => parent.mutationId === id && (parent.status === 'pending' || parent.operation === 'create')))
        && !chart.pending.some(parent => parent.operation === 'create' && parent.resourceReferenceId === item.parentReferenceId));
      if (mutation) { mutation.sealedAt = mutation.sealedAt || new Date().toISOString(); mutation.attemptCount += 1; }
    });
    if (!mutation || navigator.onLine === false || !apiReachable) return;
    const requestGeneration = connectivityGeneration;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 25000);
    const stop = () => controller.abort();
    activeSyncRequests.add(controller);
    window.addEventListener('vela:access-context-changed', stop);
    window.addEventListener('vela:offline-vault-changed', stop);
    let response;
    let body;
    const sentAt = Date.now();
    try {
      response = await fetch(`${config.api.url}/clients/${encodeURIComponent(clientReferenceId)}/chart-mutations`, {
        method: 'POST', headers: { Authorization: `Bearer ${owner.token}`, 'Content-Type': 'application/json',
          'X-Sena-Offline-Replay': 'true', 'Idempotency-Key': mutation.mutationId },
        body: JSON.stringify(requestBody(mutation)), signal: controller.signal,
      });
      body = await response.json().catch(() => ({}));
    } catch {
      assertOwner(owner);
      if (navigator.onLine !== false) reportSyncProblem(clientReferenceId, 'The server could not be reached. Your changes are retained and sync will retry.');
      await editChart(clientReferenceId, chart => {
        const current = chart.pending.find(item => item.mutationId === mutation.mutationId);
        if (current) current.nextAttemptAt = navigator.onLine === false || requestGeneration !== connectivityGeneration ? null
          : new Date(Date.now() + Math.min(300000, 1000 * 2 ** Math.min(current.attemptCount, 9))).toISOString();
      });
      return;
    } finally {
      window.clearTimeout(timeout);
      activeSyncRequests.delete(controller);
      window.removeEventListener('vela:access-context-changed', stop);
      window.removeEventListener('vela:offline-vault-changed', stop);
    }
    assertOwner(owner);
    if (body.code === 'chart_online_only') {
      await rememberChartEligibility(clientReferenceId, false);
      reportSyncProblem(clientReferenceId, ONLINE_ONLY_MESSAGE);
      return; // Retain exact mutation IDs, payloads, and dependencies. Never replay via legacy CRUD.
    }
    if (response.ok || [409, 412].includes(response.status)) reportSyncProblem(clientReferenceId, null);
    else if (response.status >= 500) reportSyncProblem(clientReferenceId, 'The server could not save these changes yet. They are retained on this device and sync will retry.');
    const clockReading = recordServerClockSample(response.headers.get('X-Sena-Server-Time'), sentAt);
    if (response.ok && body.mutationId === mutation.mutationId && body.reconciliationRequired) {
      // Compact durable receipts prove the commit, but only a fresh canonical
      // read can establish a safe base after a response was lost.
      const reconciliationController = new AbortController();
      const stopReconciliation = () => reconciliationController.abort();
      const reconciliationTimeout = window.setTimeout(stopReconciliation, 25000);
      window.addEventListener('vela:access-context-changed', stopReconciliation);
      window.addEventListener('vela:offline-vault-changed', stopReconciliation);
      try {
        const snapshot = await fetch(`${config.api.url}/clients/${encodeURIComponent(clientReferenceId)}/offline-chart?protocolVersion=1`, {
          headers: { Authorization: `Bearer ${owner.token}`, 'X-Sena-Offline-Prepare': 'true' }, signal: reconciliationController.signal,
        });
        assertOwner(owner);
        if (!snapshot.ok) throw new Error('Waiting to reconcile the confirmed server save.');
        const bundle = await snapshot.json();
        if (bundle.protocolVersion !== 1 || !Array.isArray(bundle.resources)) throw new Error('The server snapshot was incomplete.');
        const received = body.resources || [];
        body.resources = received.map(receipt => {
          const current = bundle.resources.find(item => item.resourceType === receipt.resourceType && item.referenceId === receipt.referenceId);
          return current ? { ...current, payloadIsPartial: false } : receipt;
        });
        if (mutation.operation !== 'delete' && !bundle.resources.some(item => item.resourceType === mutation.resourceType && item.referenceId === mutation.resourceReferenceId)) {
          await editChart(clientReferenceId, chart => {
            const pending = chart.pending.find(item => item.mutationId === mutation.mutationId);
            if (pending) pending.status = 'needs-review';
            const resource = chart.resources[mutation.key];
            resource.conflicts = [{ path: '', local: resource.local, message: 'The server confirmed this save, but the record is no longer available. Review the chart before linking or recreating it.' }];
          });
          return;
        }
      } catch {
        assertOwner(owner);
        await editChart(clientReferenceId, chart => {
          const pending = chart.pending.find(item => item.mutationId === mutation.mutationId);
          if (pending) pending.nextAttemptAt = new Date(Date.now() + 30000).toISOString();
        });
        return;
      } finally {
        window.clearTimeout(reconciliationTimeout);
        window.removeEventListener('vela:access-context-changed', stopReconciliation);
        window.removeEventListener('vela:offline-vault-changed', stopReconciliation);
      }
    }
    await editChart(clientReferenceId, chart => {
      const pending = chart.pending.find(item => item.mutationId === mutation.mutationId);
      if (!pending) return;
      const resource = chart.resources[mutation.key];
      const acknowledged = body.resources?.find(item => item.resourceType === mutation.resourceType
        && item.referenceId === mutation.resourceReferenceId && Number.isInteger(item.revision));
      if (response.ok && body.mutationId === mutation.mutationId && acknowledged) {
        const savedBase = applyClinicalPatch(resource.base, mutation.payload);
        const server = acknowledged?.payload ? acknowledged.payloadIsPartial === false ? acknowledged.payload : applyClinicalPatch(savedBase, acknowledged.payload) : savedBase;
        const merged = mergeClinicalValues(savedBase, resource.local, server);
        resource.base = server;
        resource.local = merged.value;
        resource.revision = acknowledged?.revision ?? resource.revision;
        resource.conflicts = retainConflicts(resource.conflicts, merged.conflicts, resource.local, server);
        resource.mrnStatus = body.mrnStatus || resource.mrnStatus;
        resource.pendingMRN = body.pendingNewbornMRN || (body.mrnStatus === 'pending' ? mutation.payload.newbornMRN : null);
        resource.clockReview = clockReviewStatus({ clockSample: clockReading || mutation.captureMetadata.clockSample }) === 'review'
          ? { ...mutation.captureMetadata, syncClockSample: clockReading } : resource.clockReview;
        resource.syncedAt = body.serverReceivedAtUtc || new Date().toISOString();
        chart.pending = chart.pending.filter(item => item.mutationId !== mutation.mutationId);
        for (const successor of chart.pending.filter(item => item.key === mutation.key && !item.sealedAt)) {
          successor.baseRevision = resource.revision;
          successor.baseValues = server;
          successor.payload = withoutConflictedFields(clinicalChanges(server, resource.local), resource.conflicts);
        }
        resource.pending = chart.pending.some(item => item.key === mutation.key);
        for (const extra of body.resources.filter(item => item !== acknowledged)) {
          const siblingKey = resourceKey(extra.resourceType, extra.referenceId);
          const sibling = chart.resources[siblingKey];
          if (!sibling || !extra.payload || chart.pending.some(item => item.key === siblingKey && item.sealedAt)) continue;
          const updated = extra.payloadIsPartial === false ? extra.payload : applyClinicalPatch(sibling.base, extra.payload);
          const rebased = mergeClinicalValues(sibling.base, sibling.local, updated);
          sibling.base = updated; sibling.local = rebased.value; sibling.revision = extra.revision;
          sibling.conflicts = retainConflicts(sibling.conflicts, rebased.conflicts, sibling.local, updated);
          for (const successor of chart.pending.filter(item => item.key === siblingKey && !item.sealedAt)) {
            successor.baseValues = updated; successor.baseRevision = extra.revision;
            successor.payload = withoutConflictedFields(clinicalChanges(updated, sibling.local), sibling.conflicts);
          }
        }
        // An aggregate receipt can already include a queued baby/alias edit.
        // Drop only never-sent updates that now match that committed state;
        // sealed retries must retain their original idempotent request.
        const satisfied = chart.pending.filter(item => item.operation === 'update' && !item.sealedAt && item.status === 'pending'
          && !Object.keys(item.payload).length);
        chart.pending = chart.pending.filter(item => !satisfied.includes(item));
        for (const item of satisfied) {
          const sibling = chart.resources[item.key];
          if (sibling) { sibling.pending = chart.pending.some(next => next.key === item.key); sibling.syncedAt = resource.syncedAt; }
        }
        mirrorSingletonLocal(chart, resource);
        return;
      }
      if (response.ok) { pending.nextAttemptAt = new Date(Date.now() + 30000).toISOString(); return; }
      if (response.status === 401) { pending.authenticationRequired = true; pending.nextAttemptAt = new Date(Date.now() + 300000).toISOString(); window.dispatchEvent(new CustomEvent('vela:session-expired')); return; }
      if ([408, 425, 429].includes(response.status) || response.status >= 500) {
        pending.nextAttemptAt = new Date(Date.now() + Math.min(300000, 1000 * 2 ** Math.min(pending.attemptCount, 9))).toISOString();
        return;
      }
      const serverResource = body.currentResource || body.current;
      // A rejected identity is terminal. Never turn an HTTP 409 into an automatic
      // new mutation ID: that can loop forever against a stable server conflict.
      pending.status = 'needs-review';
      pending.rejectionStatus = response.status;
      pending.rejectionCode = body.code || 'mutation_conflict';
      pending.nextAttemptAt = null;
      if ([409, 412].includes(response.status) && serverResource?.payload && mutation.operation === 'update') {
        const merged = mergeClinicalValues(mutation.baseValues, resource.local, serverResource.payload);
        resource.base = serverResource.payload;
        resource.local = merged.value;
        resource.revision = serverResource.revision;
        resource.conflicts = retainConflicts(resource.conflicts, merged.conflicts, resource.local, resource.base);
        if (!resource.conflicts.length) resource.conflicts = [{ path: '', local: resource.local,
          server: resource.base, currentResource: serverResource, code: body.code,
          message: mutationErrorMessage(body) }];
        // Later edits on other fields can proceed; the rejected payload stays
        // retained until the clinician explicitly reviews or discards it.
        for (const successor of chart.pending.filter(item => item.key === mutation.key && !item.sealedAt && item.status === 'pending')) {
          successor.baseValues = resource.base; successor.baseRevision = resource.revision;
          successor.payload = withoutConflictedFields(clinicalChanges(resource.base, resource.local), resource.conflicts);
          successor.dependsOn = successor.dependsOn?.filter(id => id !== mutation.mutationId);
        }
      } else {
        resource.conflicts = [{ path: '', local: resource.local, server: serverResource?.payload,
          message: mutationErrorMessage(body),
          identityConflict: mutation.operation === 'create' && response.status === 409, currentResource: serverResource, code: body.code }];
      }
      mirrorSingletonLocal(chart, resource);
    });
    if (response.ok && typeof reconcileEditorRecoveries === 'function') {
      const committed = await readChart(clientReferenceId);
      await reconcileEditorRecoveries(clientReferenceId, Object.values(committed?.resources || {}));
    }
  } finally { await releaseSlot(); await releaseChart(); }
}

export function syncClinicalChart(clientReferenceId) {
  if (!hasFreshOfflineIdentity() || !isOfflineVaultUnlocked() || !navigator.onLine || !apiReachable) return Promise.resolve();
  if (running.has(clientReferenceId)) return running.get(clientReferenceId);
  const task = synchronize(clientReferenceId).catch(error => {
    // A lock/context change intentionally stops sync. Storage failures remain
    // visible through the chart's pending state, never acknowledged as saved.
    reportSyncProblem(clientReferenceId, 'Sync could not read this device’s saved changes. Your entries are retained. Try syncing again.');
  }).finally(() => running.delete(clientReferenceId));
  running.set(clientReferenceId, task);
  return task;
}

export async function getClinicalReviewList(clientReferenceId) {
  const chart = await readChart(clientReferenceId);
  return reviewResources(chart)
    .map(resource => ({ ...resource, identityCandidates: Object.values(chart.resources).filter(candidate => candidate.resourceType === resource.resourceType
      && candidate.parentReferenceId === resource.parentReferenceId && candidate.referenceId !== resource.referenceId && candidate.revision > 0) }));
}

function reviewResources(chart) {
  return Object.values(chart?.resources || {}).filter(resource => {
    if (!(resource.conflicts?.length || resource.mrnStatus === 'pending' || resource.clockReview)) return false;
    // Show mirrored singleton field conflicts once, in the episode review.
    return !(resource.resourceType === 'birthEvent' && singletonPeer(chart, resource)?.conflicts?.length
      && !resource.conflicts.some(item => item.identityConflict) && resource.mrnStatus !== 'pending' && !resource.clockReview);
  });
}

export async function reconcileClinicalEditorCopies(clientReferenceId) {
  if (!getOfflineOwnerIdentity() || !isOfflineVaultUnlocked()) return;
  const chart = await readChart(clientReferenceId);
  if (chart && typeof reconcileEditorRecoveries === 'function') await reconcileEditorRecoveries(clientReferenceId, Object.values(chart.resources));
}

const editorCleanupRuns = new Map();
export function cleanupLegacyEditorCopies() {
  if (!hasFreshOfflineIdentity() || !isOfflineVaultUnlocked() || navigator.onLine === false) return Promise.resolve();
  const owner = identity();
  if (editorCleanupRuns.has(owner.ownerKey)) return editorCleanupRuns.get(owner.ownerKey);
  const cleanup = (async () => {
    const key = await storageKey(owner.ownerKey, 'editor-recovery-cleanup', 'stale-copies-v1');
    if (await readStoredRecord(key)) return;
    const copies = await listEditorRecoveries();
    let complete = !copies.some(copy => copy.unreadable || !copy.clientReferenceId);
    // Refresh each affected chart once. Never mark an unavailable or locked
    // device as cleaned; reconnect/unlock will retry with authoritative data.
    for (const clientReferenceId of new Set(copies.map(copy => copy.clientReferenceId).filter(Boolean))) {
      assertOwner(owner);
      try { if (!await prepareChart(clientReferenceId)) complete = false; }
      catch { complete = false; }
    }
    assertOwner(owner);
    if (complete) await compareAndSwapRecord(key, 0, { owner: owner.ownerKey, kind: 'editor-recovery-cleanup', version: 1 });
  })().finally(() => editorCleanupRuns.delete(owner.ownerKey));
  editorCleanupRuns.set(owner.ownerKey, cleanup);
  return cleanup;
}

export async function getClinicalChartStatus(clientReferenceId) {
  if (!getOfflineOwnerIdentity() || !isOfflineVaultUnlocked()) return null;
  const eligibility = await readChartEligibility(clientReferenceId);
  const chart = await readChart(clientReferenceId);
  if (eligibility?.eligible === false) return { status: 'online-only', onlineOnly: true,
    pendingCount: chart?.pending.length || 0, reviewCount: chart ? reviewResources(chart).length : 0, syncError: ONLINE_ONLY_MESSAGE };
  if (!chart) return null;
  const resources = Object.values(chart.resources);
  const reviewCount = reviewResources(chart).length;
  const times = resources.map(resource => resource.syncedAt).filter(Boolean).sort();
  return { status: reviewCount ? 'needs-review' : chart.pending.length ? 'saved-on-device' : 'synced',
    pendingCount: chart.pending.length, syncableCount: chart.pending.filter(item => item.status === 'pending').length, reviewCount, lastSyncedAt: times[times.length - 1] || null,
    authenticationRequired: Boolean(chart.pending.length && (!hasFreshOfflineIdentity() || chart.pending.some(item => item.authenticationRequired))),
    syncError: chart.pending.length ? syncProblems.get(problemKey(clientReferenceId)) || syncProblems.get(problemKey('*')) || null : null };
}

export async function discardClinicalLocalCopy({ clientReferenceId, resourceType, resourceReferenceId }) {
  const identities = new Set([String(resourceReferenceId)]);
  await editChart(clientReferenceId, chart => {
    const resource = chart.resources[resourceKey(resourceType, resourceReferenceId)];
    if (!resource) throw new Error('This local copy is no longer available.');
    const uncreated = !resource.revision;
    if (!uncreated && resource.conflicts.some(item => item.identityConflict))
      throw new Error('Resolve the record identity before choosing a server copy.');
    if (uncreated && Object.values(chart.resources).some(item => item.parentReferenceId === resourceReferenceId && !item.local?.deleted))
      throw new Error('This record has dependent chart entries. Review and link the record identity first so those entries are preserved.');
    const keys = physicalResourceKeys(chart, resource);
    if (chart.pending.some(item => keys.includes(item.key) && item.status === 'pending'))
      throw new Error('Wait for the submitted save to finish before discarding this copy.');
    chart.reviews.push({ kind: 'discarded-local-copy', resourceType, resourceReferenceId,
      local: resource.local, conflicts: resource.conflicts, reviewedAt: new Date().toISOString() });
    chart.pending = chart.pending.filter(item => !keys.includes(item.key));
    if (resource.base?.id) identities.add(String(resource.base.id));
    if (resource.base?.primaryBirthEvent?.id) identities.add(String(resource.base.primaryBirthEvent.id));
    resource.local = uncreated ? { referenceId: resourceReferenceId, deleted: true } : JSON.parse(JSON.stringify(resource.base));
    resource.conflicts = []; resource.pending = false;
    mirrorSingletonLocal(chart, resource);
  });
  const section = resourceType === 'laborEpisode' || resourceType === 'birthEvent' ? 'labor-episode'
    : resourceType.replace(/Visit$/, '').replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  const copies = (await listEditorRecoveries()).filter(copy => copy.clientReferenceId === clientReferenceId
    && copy.chartType === section && [copy.chartId, copy.state?.referenceId, copy.state?.id,
      copy.state?.primaryBirthEvent?.referenceId, copy.state?.primaryBirthEvent?.id].some(id => id != null && identities.has(String(id))));
  for (const key of new Set(copies.map(copy => copy.key))) await discardEditorRecoveries({ key, clientReferenceId });
}

export async function resolveClinicalFieldConflict({ clientReferenceId, resourceType, resourceReferenceId, path, useLocal }) {
  const result = await editChart(clientReferenceId, chart => {
    const resource = chart.resources[resourceKey(resourceType, resourceReferenceId)];
    const conflict = resource?.conflicts.find(item => item.path === path);
    if (!conflict || conflict.identityConflict) throw new Error('Review the competing record identity before linking this chart.');
    if (!path && conflict.server === undefined) throw new Error('The server copy is unavailable. Discard the local copy or reopen this chart while connected before retrying.');
    // Collection conflicts require explicit review of the entire collection.
    const keys = path.match(/[^.[\]]+/g) || [];
    if (!useLocal) {
      let target = resource.local;
      for (const key of keys.slice(0, -1)) {
        target = Array.isArray(target) ? target.find(item => String(item.referenceId ?? item.ReferenceId ?? item.clientEntryId ?? item.id ?? item.Id) === key) : target[key];
      }
      const last = keys[keys.length - 1];
      if (!keys.length) resource.local = conflict.server;
      else if (Array.isArray(target)) {
        const index = target.findIndex(item => String(item.referenceId ?? item.ReferenceId ?? item.clientEntryId ?? item.id ?? item.Id) === last);
        if (conflict.server === undefined) { if (index >= 0) target.splice(index, 1); }
        else if (index >= 0) target[index] = conflict.server;
        else target.push(conflict.server);
      } else if (conflict.server === undefined) delete target[last];
      else target[last] = conflict.server;
    }
    resource.conflicts = resource.conflicts.filter(item => item !== conflict);
    if (!resource.conflicts.length) {
      const keys = physicalResourceKeys(chart, resource);
      const rejected = chart.pending.filter(item => keys.includes(item.key) && item.status === 'needs-review');
      chart.reviews.push(...rejected.map(item => ({ kind: 'resolved-rejection', mutationId: item.mutationId,
        payload: item.payload, reviewedAt: new Date().toISOString() })));
      chart.pending = chart.pending.filter(item => !rejected.includes(item));
      for (const item of chart.pending) item.dependsOn = item.dependsOn?.filter(id => !rejected.some(prior => prior.mutationId === id));
    }
    resource.pending = chart.pending.some(item => physicalResourceKeys(chart, resource).includes(item.key));
    mirrorSingletonLocal(chart, resource);
    return { data: resource.local, base: resource.base, revision: resource.revision, parentReferenceId: resource.parentReferenceId,
      pregnancyReferenceId: resource.pregnancyReferenceId, remainingConflicts: resource.conflicts.length };
  });
  if (result.remainingConflicts) return result;
  return commitClinicalMutation({ clientReferenceId, resourceType, resourceReferenceId, operation: 'update',
    parentReferenceId: result.parentReferenceId, pregnancyReferenceId: result.pregnancyReferenceId,
    payload: result.data, baseValues: result.data, baseRevision: result.revision });
}

export async function resolveClinicalIdentityConflict({ clientReferenceId, resourceType, resourceReferenceId, targetReferenceId }) {
  await editChart(clientReferenceId, chart => {
    const oldKey = resourceKey(resourceType, resourceReferenceId);
    const source = chart.resources[oldKey];
    const conflict = source?.conflicts.find(item => item.identityConflict);
    const offered = conflict?.currentResource;
    const targetKey = resourceKey(resourceType, targetReferenceId);
    const target = (offered?.referenceId === targetReferenceId && offered.resourceType === resourceType
      ? { ...offered, base: offered.payload, local: offered.payload, conflicts: [], pending: false } : chart.resources[targetKey]);
    if (!conflict || !target || source.parentReferenceId !== target.parentReferenceId) throw new Error('Choose the existing record from the same pregnancy or parent chart.');
    if (chart.pending.some(item => item.parentReferenceId === resourceReferenceId && item.sealedAt)) throw new Error('A dependent save is still awaiting its receipt. Reconnect before linking these records.');
    const rejected = chart.pending.filter(item => item.key === oldKey);
    if (rejected.some(item => item.status !== 'needs-review' && item.sealedAt)) throw new Error('Wait for the outstanding save receipt before linking this record.');
    const merged = mergeClinicalValues(source.base, source.local, target.local);
    merged.value.referenceId = targetReferenceId;
    if (target.local.id != null) merged.value.id = target.local.id;
    target.local = merged.value;
    target.conflicts = [...(target.conflicts || []), ...merged.conflicts.filter(item => !['id', 'referenceId'].includes(item.path))];
    chart.resources[targetKey] = target;
    chart.pending = chart.pending.filter(item => item.key !== oldKey);
    const replace = value => {
      if (value === resourceReferenceId) return targetReferenceId;
      if (Array.isArray(value)) return value.map(replace);
      if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, replace(child)]));
      return value;
    };
    const reassigned = new Map();
    for (const item of chart.pending) {
      item.dependsOn = item.dependsOn?.filter(id => !rejected.some(previous => previous.mutationId === id));
      if (item.parentReferenceId !== resourceReferenceId) continue;
      item.parentReferenceId = targetReferenceId;
      item.payload = replace(item.payload);
      const newId = newOperationId();
      reassigned.set(item.mutationId, newId);
      item.mutationId = newId;
      const child = chart.resources[item.key];
      if (child) { child.parentReferenceId = targetReferenceId; child.local = replace(child.local); }
    }
    for (const item of chart.pending) item.dependsOn = item.dependsOn?.map(id => reassigned.get(id) || id);
    if (oldKey !== targetKey) delete chart.resources[oldKey];
    chart.aliases = { ...chart.aliases, [oldKey]: targetKey };
    chart.reviews.push({ kind: 'identity-link', resourceType, sourceReferenceId: resourceReferenceId,
      targetReferenceId, reviewedAt: new Date().toISOString(), localSnapshot: source.local });
    const safe = withoutConflictedFields(clinicalChanges(target.base, target.local), target.conflicts);
    if (Object.keys(safe).length) chart.pending.push({ protocolVersion: 1, mutationId: newOperationId(), key: targetKey,
      resourceType, resourceReferenceId: targetReferenceId, parentReferenceId: target.parentReferenceId,
      pregnancyReferenceId: target.pregnancyReferenceId, operation: 'update', baseRevision: target.revision,
      baseValues: target.base, payload: safe, captureMetadata: captureClinicalTime(), status: 'pending',
      createdAt: new Date().toISOString(), dependsOn: [], attemptCount: 0, nextAttemptAt: null });
    target.pending = chart.pending.some(item => item.key === targetKey);
  });
  void syncClinicalChart(clientReferenceId);
}

export async function reviewClinicalClock({ clientReferenceId, resourceType, resourceReferenceId }) {
  return editChart(clientReferenceId, chart => {
    const resource = chart.resources[resourceKey(resourceType, resourceReferenceId)];
    if (!resource?.clockReview) return;
    chart.reviews.push({ kind: 'clock-reviewed', resourceType, resourceReferenceId,
      captureMetadata: resource.clockReview, reviewedAt: new Date().toISOString() });
    resource.clockReview = null;
  });
}

export async function retryPendingMRN({ clientReferenceId, resourceReferenceId, newbornMRN, generate = false }) {
  const context = await resolveClinicalMutationContext({ clientReferenceId, resourceType: 'baby', resourceReferenceId });
  if (!context) throw new Error('Unlock this chart before updating the MRN.');
  return commitClinicalMutation({ ...context, resourceType: 'baby', operation: 'update',
    payload: generate ? { newbornMRN: null, generateNewbornMRN: true } : { newbornMRN, generateNewbornMRN: false } });
}

// A pending chart must not depend on a successful preparation fetch, or on
// another chart being readable, to resume. The existing access token authorizes
// mutations; vault tickets only resume the local encryption key after reload.
export async function retryClinicalSync({ resetBackoff = true } = {}) {
  if (navigator.onLine === false || !getOfflineOwnerIdentity()) return;
  if (!hasFreshOfflineIdentity()) {
    window.dispatchEvent(new CustomEvent('vela:session-expired'));
    announce();
    return;
  }
  if (!isOfflineVaultUnlocked()) await resumeOfflineVaultSession();
  if (!isOfflineVaultUnlocked()) return;
  const owner = identity();
  let records;
  try {
    records = (await listStoredRecords(owner.ownerKey, 'clinical-chart')).filter(record => record.pendingCount !== 0);
    reportSyncProblem('*', null);
  } catch {
    reportSyncProblem('*', 'Sync could not open device storage. Your entries are retained. Try syncing again.');
    return;
  }
  if (!records.length) return;
  if (!apiReachable) {
    if (navigator.onLine === false || !(await probeConnectivity())) return;
    apiReachable = true; ++connectivityGeneration; resetBackoff = true;
  }
  await Promise.all(records.map(async record => {
    let chart;
    try {
      chart = await decryptClinicalPayload(record.payload);
      assertOwner(owner);
      if ((await checkChartEligibility(chart.clientReferenceId))?.eligible === false) {
        reportSyncProblem(chart.clientReferenceId, ONLINE_ONLY_MESSAGE); return;
      }
      if (resetBackoff) await editChart(chart.clientReferenceId, current => {
        for (const item of current.pending) if (item.status === 'pending') { item.nextAttemptAt = null; item.authenticationRequired = false; }
      });
      let before = chart.pending.filter(item => item.status === 'pending').length;
      // Drain acknowledged dependencies immediately instead of adding two
      // seconds between each baby/episode/observation in a reconnect queue.
      for (let count = 0; count < 100; count += 1) {
        await syncClinicalChart(chart.clientReferenceId);
        const current = await readChart(chart.clientReferenceId);
        const remaining = current?.pending.filter(item => item.status === 'pending').length || 0;
        if (!current || remaining >= before || !remaining) break;
        before = remaining;
      }
    } catch {
      // One damaged/older record must never stop another chart from syncing.
      reportSyncProblem(chart?.clientReferenceId || record.clientReferenceId || '*', 'Sync could not read a saved device chart. Your entries are retained. Try syncing again.');
    }
  }));
}

export function installClinicalSyncTriggers() {
  if (triggerInstalled) return;
  triggerInstalled = true;
  const safeSync = event => {
    void cleanupLegacyEditorCopies().catch(() => {});
    void retryClinicalSync({ resetBackoff: ['online', 'focus', 'visibilitychange', 'vela:api-reachability', 'vela:logged-in', 'vela:offline-vault-changed', 'storage'].includes(event?.type) }).catch(() => {
    reportSyncProblem('*', 'Sync could not resume. Your entries are retained. Try syncing again.');
  }); };
  void cleanupLegacyEditorCopies().catch(() => {});
  window.addEventListener('offline', () => {
    apiReachable = false; ++connectivityGeneration;
    activeSyncRequests.forEach(controller => controller.abort());
  });
  window.addEventListener('online', event => { apiReachable = true; ++connectivityGeneration; safeSync(event); });
  window.addEventListener('vela:api-reachability', event => {
    const reachable = event.detail?.reachable;
    if (typeof reachable !== 'boolean' || reachable === apiReachable) return;
    apiReachable = reachable; ++connectivityGeneration;
    if (reachable) safeSync(event);
    else activeSyncRequests.forEach(controller => controller.abort());
  });
  document.addEventListener('visibilitychange', event => { if (document.visibilityState === 'visible') safeSync(event); });
  window.addEventListener('focus', safeSync);
  window.addEventListener('vela:logged-in', safeSync);
  window.addEventListener('vela:offline-vault-changed', safeSync);
  window.addEventListener('storage', event => { if (event.key === 'velaToken') safeSync(event); });
  window.setInterval(safeSync, 2000);
  // An online reload may have pending work before a chart is opened.
  safeSync();
}
